// AuthContext — the single source of truth for the current user across the app.
// Wraps Firebase onAuthStateChanged, syncs the local user record (UserOut) via
// /api/auth/firebase-sync, and exposes login/signup/logout helpers.
//
// Other components keep working: localStorage 'token' is populated with the
// Firebase ID token (auto-refreshed) and 'user' with the local UserOut JSON,
// so existing axiosInstance and any code that reads localStorage still works.
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithCredential,
  GoogleAuthProvider,
  signOut as fbSignOut,
  sendPasswordResetEmail,
  updateProfile,
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import axios from 'axios';
import { axiosInstance } from '../config/axios';
import { auth, googleProvider } from '../lib/firebase';
import { signInWithApple as runAppleSignIn } from '../lib/appleAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};

async function syncWithBackend(idToken) {
  // Posts the Firebase ID token to /auth/firebase-sync to get the local UserOut.
  // Returns null if no local user exists yet (caller should call signup flow).
  try {
    const res = await axios.post(
      `${API}/auth/firebase-sync`,
      { id_token: idToken },
      { headers: { 'Content-Type': 'application/json' } }
    );
    return res.data;
  } catch (e) {
    if (e.response?.status === 404) return null;
    throw e;
  }
}

async function registerWithBackend(idToken, name, mobileNumber) {
  const res = await axios.post(
    `${API}/auth/firebase-register`,
    { id_token: idToken, name, mobile_number: mobileNumber },
    { headers: { 'Content-Type': 'application/json' } }
  );
  return res.data;
}

export const AuthProvider = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [user, setUser] = useState(null);              // local UserOut
  const [loading, setLoading] = useState(true);

  // Subscribe to Firebase auth state once on mount
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);
      if (!fbUser) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setLoading(false);
        return;
      }
      try {
        const idToken = await fbUser.getIdToken();
        localStorage.setItem('token', idToken);
        const local = await syncWithBackend(idToken);
        if (local) {
          localStorage.setItem('user', JSON.stringify(local));
          setUser(local);
        } else {
          // Firebase account exists but no local user row yet — caller will
          // hit the registration flow which calls completeSignup().
          setUser(null);
        }
      } catch (e) {
        console.error('Auth sync failed:', e);
        setUser(null);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, []);

  // Refresh ID token periodically (Firebase tokens expire every ~1h)
  useEffect(() => {
    if (!firebaseUser) return;
    const refresh = async () => {
      try {
        const t = await firebaseUser.getIdToken(true);
        localStorage.setItem('token', t);
      } catch (e) {
        console.warn('Token refresh failed:', e?.message);
      }
    };
    const interval = setInterval(refresh, 50 * 60 * 1000); // 50 min
    return () => clearInterval(interval);
  }, [firebaseUser]);

  const login = useCallback(async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const idToken = await cred.user.getIdToken();
    const local = await syncWithBackend(idToken);
    if (!local) {
      // Edge case: Firebase user exists but no local row — shouldn't happen
      // for migrated users. Surface a clear error.
      throw new Error('Account is registered but missing profile data. Contact support.');
    }
    localStorage.setItem('token', idToken);
    localStorage.setItem('user', JSON.stringify(local));
    setUser(local);
    return local;
  }, []);

  const signup = useCallback(async (email, password, name, mobileNumber) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    if (name) {
      try { await updateProfile(cred.user, { displayName: name }); } catch (_e) { /* non-fatal */ }
    }
    const idToken = await cred.user.getIdToken();
    const local = await registerWithBackend(idToken, name, mobileNumber);
    localStorage.setItem('token', idToken);
    localStorage.setItem('user', JSON.stringify(local));
    setUser(local);
    return local;
  }, []);

  const loginWithGoogle = useCallback(async () => {
    let cred;
    if (Capacitor.isNativePlatform()) {
      // Native path: Capacitor WebViews block popups, so the web OAuth redirect
      // flow dies at accounts.google.com with a 400. Use the native Google
      // Sign-In sheet (respects the SHA-1s registered in Firebase Console),
      // then bridge the Google ID token into the Firebase web SDK.
      const result = await FirebaseAuthentication.signInWithGoogle();
      const credential = GoogleAuthProvider.credential(result.credential?.idToken);
      cred = await signInWithCredential(auth, credential);
    } else {
      cred = await signInWithPopup(auth, googleProvider);
    }
    const idToken = await cred.user.getIdToken();
    let local = await syncWithBackend(idToken);
    if (!local) {
      // First-time Google login → create local user row.
      // Mobile number can be collected post-signup via profile flow.
      local = await registerWithBackend(
        idToken,
        cred.user.displayName || cred.user.email.split('@')[0],
        '' // mobile collected later in profile completion
      );
    }
    localStorage.setItem('token', idToken);
    localStorage.setItem('user', JSON.stringify(local));
    setUser(local);
    return local;
  }, []);

  const loginWithApple = useCallback(async () => {
    const fbUser = await runAppleSignIn();
    const idToken = await fbUser.getIdToken();
    let local = await syncWithBackend(idToken);
    if (!local) {
      // First-time Apple login → register the local row. Apple gives us the
      // display name only on the very first authorization; afterwards email
      // is the only stable identifier.
      const fallbackName =
        fbUser.displayName ||
        (fbUser.email ? fbUser.email.split('@')[0] : 'Backyard User');
      local = await registerWithBackend(idToken, fallbackName, '');
    }
    localStorage.setItem('token', idToken);
    localStorage.setItem('user', JSON.stringify(local));
    setUser(local);
    return local;
  }, []);

  const sendReset = useCallback(async (email) => {
    await sendPasswordResetEmail(auth, email);
  }, []);

  const logout = useCallback(async () => {
    // Best-effort: unregister this device's push token so the user doesn't
    // keep receiving pushes from a session they've left.
    try {
      const { disablePush } = await import('../lib/firebaseMessaging');
      await disablePush();
    } catch { /* non-fatal */ }
    await fbSignOut(auth);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setFirebaseUser(null);
  }, []);

  // Profile changes made anywhere (profile page, "complete your details"
  // sheet) go through here so every screen sees the same user.
  const updateUser = useCallback((next) => {
    if (!next) return;
    localStorage.setItem('user', JSON.stringify(next));
    setUser(next);
  }, []);

  const refreshUser = useCallback(async () => {
    const res = await axiosInstance.get('/auth/me');
    updateUser(res.data);
    return res.data;
  }, [updateUser]);

  const value = {
    firebaseUser,
    user,
    loading,
    updateUser,
    refreshUser,
    login,
    signup,
    loginWithGoogle,
    loginWithApple,
    sendReset,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
